<template>
  <div class="mt-4">
    <h3 class="darkgrey--text">
      Finansieringsplan for kjøp av leiligheten
    </h3>
    <p class="mb-6">
      Fullt oppgjør ved ferdigstillelse av bolig
    </p>

    <v-radio-group
      v-if="data"
      v-model="data.type"
      class="pt-0 mb-4"
      row
      :rules="[v => !!v || 'Finansiering må være valgt']"
      @change="onFinancingOptionChange"
    >
      <v-radio
        v-for="option in financingOptions"
        :key="option.key"
        :label="option.value"
        :value="option.key"
      ></v-radio>
    </v-radio-group>

    <p v-if="financingAdditionalInfo" class="mb-6 darkgrey--text font-italic">
      {{ financingAdditionalInfo }}
    </p>

    <v-row v-if="data && data.type && data.type !== 4">
      <v-col class="py-0" cols="12" md="6">
        <v-text-field
          v-model="data.loanBankName"
          :rules="[loanBankNameRule]"
          :readonly="isReadonlyFinancingField('loanBankName')"
          label="Lån/mellomfinansiering i bank"
          prepend-inner-icon="mdi-bank"
          placeholder="Navn til banken"
          required
          filled
          autocomplete="off"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" md="6">
        <v-text-field
          v-model="data.loanBankContactName"
          :rules="[loanBankContactNameRule]"
          :readonly="isReadonlyFinancingField('loanBankContactName')"
          label="Saksbehandler bank"
          placeholder="Navn til saksbehandler"
          prepend-inner-icon="mdi-account"
          required
          filled
          autocomplete="off"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" md="6">
        <MobileNumber
          v-model="data.loanBankContactPhone"
          :readonly="isReadonlyFinancingField('loanBankContactPhone')"
          label="Saksbehandler mobil"
          filled
          prepend-inner-icon="mdi-phone"
          autocomplete="off"
        />
      </v-col>

      <v-col class="py-0" cols="12" md="6">
        <v-text-field
          v-model="data.loanBankContactEmail"
          :rules="[loanBankContactEmailRule]"
          :readonly="isReadonlyFinancingField('loanBankContactEmail')"
          label="Saksbehandler e-post"
          placeholder="navn@eksempel.no"
          prepend-inner-icon="mdi-email"
          required
          filled
          autocomplete="off"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" md="6">
        <v-text-field
          v-model.number="loanAmountFormated"
          :readonly="isReadonlyFinancingField('loanAmount')"
          label="Lånesum"
          prepend-inner-icon="mdi-cash-plus"
          :rules="[v => !!v || 'Lånesum må fylles ut!']"
          suffix="Kr"
          placeholder="Skriv riktig sum uten tegn og mellomrom"
          required
          filled
          autocomplete="off"
          @blur="loanAmountFocus = false"
          @focus="loanAmountFocus = true"
          @input="AddToTotal"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" md="6">
        <v-text-field
          v-model.number="loanEquityAmountFormated"
          :readonly="isReadonlyFinancingField('loanEquityAmount')"
          label="Egenkapital"
          prepend-inner-icon="mdi-account-cash"
          suffix="Kr"
          :rules="[v => !!v || 'Egenkapital må fylles ut!']"
          required
          placeholder="Skriv riktig sum uten tegn og mellomrom"
          filled
          autocomplete="off"
          @blur="loanEquityAmountFocus = false"
          @focus="loanEquityAmountFocus = true"
          @input="AddToTotal"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12">
        <v-text-field
          v-model="loanBankTotalFormated"
          :rules="[totalRule]"
          label="Til sammen"
          prepend-inner-icon="mdi-cash-multiple"
          suffix="Kr"
          readonly
          filled
          autocomplete="off"
        ></v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import format from "@/helpers/format";
import MobileNumber from "@/components/shared/textField/MobileNumber";

//Fields on a financingOptionsInfo element that can populate the form
const FINANCING_INFO_FIELDS = [
  "loanBankName",
  "loanBankContactName",
  "loanBankContactPhone",
  "loanBankContactEmail"
];

//MobileNumber expects an integer, the api returns the number as a string
function toPhoneNumber(value) {
  var digits = ("" + value).replace(/\D/g, "");

  //Remove country code, +47 xx xx xx xx or 0047 xx xx xx xx
  if (digits.length === 12 && digits.indexOf("0047") === 0) {
    digits = digits.substring(4);
  } else if (digits.length === 10 && digits.indexOf("47") === 0) {
    digits = digits.substring(2);
  }

  var number = parseInt(digits);

  return isNaN(number) ? null : number;
}

export default {
  props: {
    value: {
      required: true
    },
    isValidating: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loanEquityAmountFocus: false,
      loanAmountFocus: false,
      nameRules: [
        v => !!v || "Name is required",
        v => (v && v.length <= 10) || "Name must be less than 10 characters"
      ],
      emailRules: [
        v => !!v || "E-post må fylles ut",
        v => /.+@.+\..+/.test(v) || "E-post må være gyldig"
      ],
      format
    };
  },
  mounted() {
    //Make type reactive so readonly/info follows the selected option
    if (this.data && this.data.type === undefined) {
      this.$set(this.data, "type", null);
    }
    this.applyFinancingOptionInfo(false);
  },
  methods: {
    isReadonlyFinancingField(field) {
      return this.readonlyFinancingFields.indexOf(field) !== -1;
    },
    //Value from the matching financingOptionsInfo element, null when missing
    financingInfoValue(info, field) {
      var value = info ? info[field] : null;

      if (value === null || value === undefined || value === "") {
        return null;
      }

      return field === "loanBankContactPhone" ? toPhoneNumber(value) : value;
    },
    onFinancingOptionChange() {
      this.applyFinancingOptionInfo(true);
    },
    //Populate the fields from the matching financingOptionsInfo element.
    //Fields without a match are cleared when the user changes option.
    applyFinancingOptionInfo(clearFieldsWithoutInfo) {
      if (!this.data) {
        return;
      }

      var info = this.selectedFinancingOptionInfo;

      FINANCING_INFO_FIELDS.forEach(field => {
        var value = this.financingInfoValue(info, field);

        if (value !== null) {
          this.$set(this.data, field, value);
        } else if (clearFieldsWithoutInfo) {
          this.$set(this.data, field, null);
        }
      });

      if (info || clearFieldsWithoutInfo) {
        this.AddToTotal();
      }
    },
    AddToTotal() {
      var tot =
        (this.data.loanAmount ? this.data.loanAmount : 0) +
        (this.data.loanEquityAmount ? this.data.loanEquityAmount : 0);

      this.$set(this.data, "loanBankTotal", tot);
    }
  },
  computed: {
    ...mapGetters({
      totalPrice: "purchase/totalPrice",
      sectionInfo: "purchase/sectionInfo"
    }),
    financingOptions() {
      return this.sectionInfo && this.sectionInfo.financingOptions
        ? this.sectionInfo.financingOptions
        : [];
    },
    financingOptionsInfo() {
      return this.sectionInfo && this.sectionInfo.financingOptionsInfo
        ? this.sectionInfo.financingOptionsInfo
        : [];
    },
    selectedFinancingOptionInfo() {
      if (
        !this.data ||
        this.data.type === null ||
        this.data.type === undefined
      ) {
        return null;
      }

      var info = this.financingOptionsInfo.find(
        x => x.financeOption === this.data.type
      );

      return info ? info : null;
    },
    financingAdditionalInfo() {
      return this.selectedFinancingOptionInfo
        ? this.selectedFinancingOptionInfo.additionalInfo
        : null;
    },
    readonlyFinancingFields() {
      var info = this.selectedFinancingOptionInfo;
      if (!info) {
        return [];
      }

      return FINANCING_INFO_FIELDS.filter(
        field => this.financingInfoValue(info, field) !== null
      );
    },
    data: {
      get() {
        return this.value;
      },
      set(data) {
        this.$emit("input", data);
      }
    },
    loanBankNameRule() {
      return v => {
        return !!v || "Bankens navn må fylles ut!";
      };
    },
    loanBankContactNameRule() {
      return v => {
        return !!v || "Kundekontakt i banken må fylles ut!";
      };
    },
    loanBankContactEmailRule() {
      return v => {
        return !!v || "E-post til banken må fylles ut!";
      };
    },
    totalRule() {
      return () => {
        return (
          (!!this.data &&
            !!this.data.loanBankTotal &&
            this.data.loanBankTotal >= this.totalPrice) ||
          "Totalprisen på boligen må være lik lånesum + egenkapital"
        );
      };
    },

    loanEquityAmountFormated: {
      get() {
        if (this.data) {
          if (this.loanEquityAmountFocus) {
            return this.data.loanEquityAmount
              ? this.data.loanEquityAmount
              : null;
          } else {
            return this.data.loanEquityAmount
              ? format.numberFormat(this.data.loanEquityAmount)
              : null;
          }
        }
        return null;
      },
      set(value) {
        this.$set(this.data, "loanEquityAmount", value);
      }
    },
    loanAmountFormated: {
      get() {
        if (this.data) {
          if (this.loanAmountFocus) {
            return this.data.loanAmount ? this.data.loanAmount : null;
          } else {
            return this.data.loanAmount
              ? format.numberFormat(this.data.loanAmount)
              : null;
          }
        }
        return null;
      },
      set(value) {
        this.$set(this.data, "loanAmount", value);
      }
    },
    loanBankTotalFormated() {
      if (this.data) {
        return this.data.loanBankTotal
          ? format.numberFormat(this.data.loanBankTotal)
          : null;
      }
      return null;
    }
  },
  components: {
    MobileNumber
  }
};
</script>
